import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import DialogContentWrapper from "../../../common/components/functional/dialogs/DialogContentWrapper";
import { useTranslation } from "react-i18next";
import {
  TesSalaryTableDocument,
  TesSalaryTableDraftContent,
  TesSalaryTableCell,
  TesSalaryTableColumn,
  TesSalaryTableRow,
} from "../../tesSalaryTables/tesSalaryTablesApi";
import TesSalaryTableDecimalInput from "./TesSalaryTableDecimalInput";
import TesSalaryTableOrderIndicator from "./TesSalaryTableOrderIndicator";
import {
  DropTarget,
  getDropIndicatorRowSx,
  getHorizontalDropPosition,
  getVerticalDropPosition,
} from "./tesSalaryTableEditorDnd";
import {
  emptyTesSalaryTableDraft,
  getNextLabelFromLast,
  getTesSalaryTableDraftCell,
  nextCompactId,
  normalizeDraftForComparison,
  removeDraftColumn,
  removeDraftRow,
  reorderDraftColumns,
  reorderDraftRows,
  toDraftContent,
  upsertTesSalaryTableDraftCell,
} from "./tesSalaryTableEditorHelpers";

type Props = {
  mode: "create" | "edit" | "copy" | "view";
  open: boolean;
  onClose: () => void;
  onSave: (content: TesSalaryTableDraftContent) => void | Promise<unknown>;
  table?: TesSalaryTableDocument | null;
  tesName?: string;
  workTitleOptions: { id: string; name: string }[];
};

type UndoDeleteState =
  | {
      type: "row";
      item: TesSalaryTableRow;
      index: number;
      cells: TesSalaryTableCell[];
    }
  | {
      type: "column";
      item: TesSalaryTableColumn;
      index: number;
      cells: TesSalaryTableCell[];
    };

const TesSalaryTableEditorDialog = ({
  mode,
  open,
  onClose,
  onSave,
  table,
  tesName,
  workTitleOptions,
}: Props) => {
  const { t } = useTranslation("settings");
  const [draft, setDraft] = useState<TesSalaryTableDraftContent>(
    emptyTesSalaryTableDraft(),
  );
  const [initialDraft, setInitialDraft] = useState<TesSalaryTableDraftContent>(
    emptyTesSalaryTableDraft(),
  );
  const [salaryViewMode, setSalaryViewMode] = useState<
    "all" | "hourly" | "monthly"
  >("all");
  const [draggedRowId, setDraggedRowId] = useState<string | null>(null);
  const [draggedColumnId, setDraggedColumnId] = useState<string | null>(null);
  const [rowDropTarget, setRowDropTarget] = useState<DropTarget>(null);
  const [columnDropTarget, setColumnDropTarget] = useState<DropTarget>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [undoDeleteState, setUndoDeleteState] =
    useState<UndoDeleteState | null>(null);
  const [hourlyShortcutMode, setHourlyShortcutMode] = useState<
    "amount" | "percent" | null
  >(null);
  const [hourlyShortcutValue, setHourlyShortcutValue] = useState("");
  const [
    roundCalculatedMonthlySalaryToFullEur,
    setRoundCalculatedMonthlySalaryToFullEur,
  ] = useState(false);
  const readOnly = mode === "view";

  useEffect(() => {
    if (open) {
      const nextDraft = toDraftContent(table);
      const normalizedCopyName = nextDraft.name.trim()
        // mode === "copy" && nextDraft.name.trim()
        //   ? nextDraft.name.trim().endsWith(` (1)`)
        //     ? nextDraft.name
        //     : `${nextDraft.name} (1)`
        //   : nextDraft.name;
      setInitialDraft({
        ...nextDraft,
        name: normalizedCopyName,
      });
      setDraft({
        ...nextDraft,
        name: normalizedCopyName,
      });
      setUndoDeleteState(null);
      setIsSaving(false);
      setHourlyShortcutMode(null);
      setHourlyShortcutValue("");
      setRoundCalculatedMonthlySalaryToFullEur(false);
    }
  }, [open, table, mode]);

  const salaryCellNeedsMonthlyDivider = useMemo(
    () =>
      draft.cells.some((cell) => {
        const hasHourly = !!cell.hourlySalary?.trim();
        const hasMonthly = !!cell.monthlySalary?.trim();
        return hasHourly !== hasMonthly;
      }),
    [draft.cells],
  );
  const monthlyDividerMissingFromProjection =
    mode !== "create" && salaryCellNeedsMonthlyDivider && !draft.monthlyDivider;
  const monthlyDividerNumber = Number(draft.monthlyDivider || "");
  const hasValidMonthlyDivider =
    Number.isFinite(monthlyDividerNumber) && monthlyDividerNumber > 0;
  const isDirty = useMemo(
    () =>
      JSON.stringify(normalizeDraftForComparison(draft)) !==
      JSON.stringify(normalizeDraftForComparison(initialDraft)),
    [draft, initialDraft],
  );

  const saveDisabled =
    isSaving ||
    !draft.name ||
    !draft.effectiveDate ||
    draft.rows.length === 0 ||
    draft.columns.length === 0 ||
    monthlyDividerMissingFromProjection ||
    (mode === "edit" && !isDirty);

  const parseDecimalChangeInput = (value: string) =>
    Number(value.replace(",", "."));

  const formatMoney = (value: number) => value.toFixed(2);

  const applyHourlyShortcut = () => {
    if (!hourlyShortcutMode) return;
    const parsedChange = parseDecimalChangeInput(hourlyShortcutValue.trim());
    if (!Number.isFinite(parsedChange)) return;

    setDraft((current) => {
      let next = current;
      current.rows.forEach((row) => {
        current.columns.forEach((column) => {
          const existing = getTesSalaryTableDraftCell(
            next.cells,
            row.rowId,
            column.columnId,
          )?.hourlySalary;
          if (!existing?.trim()) return;

          const currentHourly = parseDecimalChangeInput(existing);
          if (!Number.isFinite(currentHourly)) return;

          const changedValue =
            hourlyShortcutMode === "amount"
              ? currentHourly + parsedChange
              : currentHourly * (1 + parsedChange / 100);

          next = upsertTesSalaryTableDraftCell({
            draft: next,
            rowId: row.rowId,
            columnId: column.columnId,
            field: "hourlySalary",
            value: formatMoney(changedValue),
            monthlyDivider: hasValidMonthlyDivider
              ? monthlyDividerNumber
              : null,
            monthlySalaryScale: roundCalculatedMonthlySalaryToFullEur ? 0 : 2,
          });
        });
      });
      return next;
    });
    setHourlyShortcutMode(null);
    setHourlyShortcutValue("");
  };

  return (
    <DialogContentWrapper
      title={
        mode === "create"
          ? t("tesSalaryTablesCreateDraft")
          : mode === "view"
            ? t("tesSalaryTablesViewTable")
            : mode === "copy"
              ? t("tesSalaryTablesCopyDraft")
              : t("tesSalaryTablesEditDraft")
      }
      onCancel={onClose}
      onSave={
        readOnly
          ? undefined
          : () => {
              if (isSaving) return;
              setIsSaving(true);
              Promise.resolve(onSave(draft)).finally(() => setIsSaving(false));
            }
      }
      saveDisabled={saveDisabled}
      draggable={false}
      containerSx={{ minWidth: 1100, maxWidth: 1200 }}
      GridContainerProps={{ rowSpacing: 2 }}
      customCancelButtonLabel={readOnly ? t("common:close") : undefined}
    >
      <Grid item xs={4} textAlign="end" alignSelf="center">
        <Typography variant="secondary">{t("common:name")}</Typography>
      </Grid>
      <Grid item xs={8}>
        <TextField
          fullWidth
          size="small"
          value={draft.name}
          disabled={readOnly}
          onChange={(event) =>
            setDraft((current) => ({ ...current, name: event.target.value }))
          }
        />
      </Grid>

      <Grid item xs={4} textAlign="end" alignSelf="center">
        <Typography variant="secondary">{t("common:tes")}</Typography>
      </Grid>
      <Grid item xs={8}>
        <TextField fullWidth size="small" value={tesName || ""} disabled />
      </Grid>

      <Grid item xs={4} textAlign="end" alignSelf="center">
        <Typography variant="secondary">
          {t("tesSalaryTablesEffectiveDate")}
        </Typography>
      </Grid>
      <Grid item xs={8}>
        <DesktopDatePicker
          value={new Date(draft.effectiveDate)}
          format="dd.MM.yyyy"
          disabled={readOnly}
          onChange={(value) => {
            if (!value) return;
            setDraft((current) => ({
              ...current,
              effectiveDate: format(value, "yyyy-MM-dd"),
            }));
          }}
          slots={{ textField: TextField }}
          slotProps={{ textField: { size: "small", fullWidth: true } }}
        />
      </Grid>

      <Grid item xs={4} textAlign="end" alignSelf="center">
        <Typography variant="secondary">
          {t("tesSalaryTablesMonthlyDivider")}
        </Typography>
      </Grid>
      <Grid item xs={8}>
        <TesSalaryTableDecimalInput
          value={draft.monthlyDivider || ""}
          disabled={readOnly}
          onChange={(value) =>
            setDraft((current) => ({
              ...current,
              monthlyDivider: value,
            }))
          }
        />
        <Typography variant="caption" color="text.secondary">
          {t("tesSalaryTablesMonthlyDividerHelp")}
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={roundCalculatedMonthlySalaryToFullEur}
              disabled={readOnly || !hasValidMonthlyDivider}
              onChange={(event) =>
                setRoundCalculatedMonthlySalaryToFullEur(event.target.checked)
              }
            />
          }
          label={t("tesSalaryTablesRoundCalculatedMonthlySalaryToFullEur")}
        />
      </Grid>

      <Grid item xs={4} textAlign="end" alignSelf="center">
        <Typography variant="secondary">{t("common:workTitles")}</Typography>
      </Grid>
      <Grid item xs={8}>
        <Autocomplete
          multiple
          size="small"
          disabled={readOnly}
          options={workTitleOptions}
          value={workTitleOptions.filter((option) =>
            draft.workTitleIds.includes(option.id),
          )}
          getOptionLabel={(option) => option.name}
          onChange={(_, value) =>
            setDraft((current) => ({
              ...current,
              workTitleIds: value.map((item) => item.id),
            }))
          }
          renderInput={(params) => <TextField {...params} />}
        />
      </Grid>

      <Grid item xs={4} textAlign="end" alignSelf="center">
        <Typography variant="secondary">
          {t("tesSalaryTablesVisibleSalaryInputs")}
        </Typography>
      </Grid>
      <Grid item xs={8}>
        <ToggleButtonGroup
          exclusive
          value={salaryViewMode}
          onChange={(_, value) => {
            if (value) {
              setSalaryViewMode(value);
            }
          }}
          size="small"
        >
          <ToggleButton value="all">{t("common:all")}</ToggleButton>
          <ToggleButton value="hourly">
            {t("talentProfile:hourlyWage")}
          </ToggleButton>
          <ToggleButton value="monthly">
            {t("talentProfile:monthlySalary")}
          </ToggleButton>
        </ToggleButtonGroup>
      </Grid>

      {monthlyDividerMissingFromProjection && (
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ padding: 2, color: "warning.main" }}>
            {t("tesSalaryTablesMonthlyDividerProjectionWarning")}
          </Paper>
        </Grid>
      )}

      <Grid item xs={12}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{t("tesSalaryTablesRows")}</Typography>
          {!readOnly && (
            <Button
              variant="outlined"
              onClick={() =>
                setDraft((current) => ({
                  ...current,
                  rows: [
                    ...current.rows,
                    {
                      rowId: nextCompactId(
                        "r",
                        current.rows.map((row) => row.rowId),
                      ),
                      label: getNextLabelFromLast(current.rows),
                      secondaryLabel: "",
                      sortOrder: current.rows.length,
                    },
                  ],
                }))
              }
            >
              {t("tesSalaryTablesAddRow")}
            </Button>
          )}
        </Box>
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ marginTop: 1 }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("tesSalaryTablesLabel")}</TableCell>
                <TableCell>{t("tesSalaryTablesSecondaryLabel")}</TableCell>
                <TableCell>{t("tesSalaryTablesSortOrder")}</TableCell>
                <TableCell width={60} />
              </TableRow>
            </TableHead>
            <TableBody>
              {draft.rows.map((row, index) => (
                <TableRow
                  key={row.rowId}
                  draggable={!readOnly}
                  onDragStart={() => {
                    setDraggedRowId(row.rowId);
                    setRowDropTarget(null);
                  }}
                  onDragEnd={() => {
                    setDraggedRowId(null);
                    setRowDropTarget(null);
                  }}
                  onDragOver={(event) => {
                    if (readOnly || !draggedRowId) return;
                    event.preventDefault();
                    if (draggedRowId !== row.rowId) {
                      setRowDropTarget({
                        id: row.rowId,
                        position: getVerticalDropPosition(
                          event.clientY,
                          event.currentTarget.getBoundingClientRect(),
                        ),
                      });
                    }
                  }}
                  onDragLeave={() => {
                    if (rowDropTarget?.id === row.rowId) {
                      setRowDropTarget(null);
                    }
                  }}
                  onDrop={() => {
                    if (readOnly || !draggedRowId || !rowDropTarget) return;
                    setDraft((current) =>
                      reorderDraftRows({
                        draft: current,
                        sourceRowId: draggedRowId,
                        targetRowId: row.rowId,
                        position: rowDropTarget.position,
                      }),
                    );
                    setDraggedRowId(null);
                    setRowDropTarget(null);
                  }}
                  sx={getDropIndicatorRowSx(
                    readOnly,
                    draggedRowId === row.rowId,
                    rowDropTarget?.id === row.rowId &&
                      rowDropTarget.position === "before",
                    rowDropTarget?.id === row.rowId &&
                      rowDropTarget.position === "after",
                  )}
                >
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      value={row.label}
                      disabled={readOnly}
                      onChange={(event) =>
                        setDraft((current) => ({
                          ...current,
                          rows: current.rows.map((item) =>
                            item.rowId === row.rowId
                              ? { ...item, label: event.target.value }
                              : item,
                          ),
                        }))
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      value={row.secondaryLabel || ""}
                      disabled={readOnly}
                      onChange={(event) =>
                        setDraft((current) => ({
                          ...current,
                          rows: current.rows.map((item) =>
                            item.rowId === row.rowId
                              ? {
                                  ...item,
                                  secondaryLabel: event.target.value,
                                }
                              : item,
                          ),
                        }))
                      }
                    />
                  </TableCell>
                  <TableCell width={120}>
                    <TesSalaryTableOrderIndicator
                      readOnly={readOnly}
                      index={index}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {!readOnly && (
                      <IconButton
                        aria-label={`${t("delete")} ${t("tesSalaryTablesRow")}`}
                        onClick={() =>
                          setDraft((current) => {
                            const indexToRemove = current.rows.findIndex(
                              (item) => item.rowId === row.rowId,
                            );
                            if (indexToRemove < 0) return current;

                            setUndoDeleteState({
                              type: "row",
                              item: current.rows[indexToRemove],
                              index: indexToRemove,
                              cells: current.cells.filter(
                                (cell) => cell.rowId === row.rowId,
                              ),
                            });

                            return removeDraftRow(current, row.rowId);
                          })
                        }
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      <Grid item xs={12}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{t("tesSalaryTablesColumns")}</Typography>
          {!readOnly && (
            <Button
              variant="outlined"
              onClick={() =>
                setDraft((current) => ({
                  ...current,
                  columns: [
                    ...current.columns,
                    {
                      columnId: nextCompactId(
                        "c",
                        current.columns.map((column) => column.columnId),
                      ),
                      label: getNextLabelFromLast(current.columns),
                      secondaryLabel: "",
                      sortOrder: current.columns.length,
                    },
                  ],
                }))
              }
            >
              {t("tesSalaryTablesAddColumn")}
            </Button>
          )}
        </Box>
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ marginTop: 1 }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("tesSalaryTablesLabel")}</TableCell>
                <TableCell>{t("tesSalaryTablesSecondaryLabel")}</TableCell>
                <TableCell>{t("tesSalaryTablesSortOrder")}</TableCell>
                <TableCell width={60} />
              </TableRow>
            </TableHead>
            <TableBody>
              {draft.columns.map((column, index) => (
                <TableRow
                  key={column.columnId}
                  draggable={!readOnly}
                  onDragStart={() => {
                    setDraggedColumnId(column.columnId);
                    setColumnDropTarget(null);
                  }}
                  onDragEnd={() => {
                    setDraggedColumnId(null);
                    setColumnDropTarget(null);
                  }}
                  onDragOver={(event) => {
                    if (readOnly || !draggedColumnId) return;
                    event.preventDefault();
                    if (draggedColumnId !== column.columnId) {
                      setColumnDropTarget({
                        id: column.columnId,
                        position: getHorizontalDropPosition(
                          event.clientX,
                          event.currentTarget.getBoundingClientRect(),
                        ),
                      });
                    }
                  }}
                  onDragLeave={() => {
                    if (columnDropTarget?.id === column.columnId) {
                      setColumnDropTarget(null);
                    }
                  }}
                  onDrop={() => {
                    if (readOnly || !draggedColumnId || !columnDropTarget) {
                      return;
                    }
                    setDraft((current) =>
                      reorderDraftColumns({
                        draft: current,
                        sourceColumnId: draggedColumnId,
                        targetColumnId: column.columnId,
                        position: columnDropTarget.position,
                      }),
                    );
                    setDraggedColumnId(null);
                    setColumnDropTarget(null);
                  }}
                  sx={getDropIndicatorRowSx(
                    readOnly,
                    draggedColumnId === column.columnId,
                    columnDropTarget?.id === column.columnId &&
                      columnDropTarget.position === "before",
                    columnDropTarget?.id === column.columnId &&
                      columnDropTarget.position === "after",
                  )}
                >
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      value={column.label}
                      disabled={readOnly}
                      onChange={(event) =>
                        setDraft((current) => ({
                          ...current,
                          columns: current.columns.map((item) =>
                            item.columnId === column.columnId
                              ? { ...item, label: event.target.value }
                              : item,
                          ),
                        }))
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      value={column.secondaryLabel || ""}
                      disabled={readOnly}
                      onChange={(event) =>
                        setDraft((current) => ({
                          ...current,
                          columns: current.columns.map((item) =>
                            item.columnId === column.columnId
                              ? {
                                  ...item,
                                  secondaryLabel: event.target.value,
                                }
                              : item,
                          ),
                        }))
                      }
                    />
                  </TableCell>
                  <TableCell width={120}>
                    <TesSalaryTableOrderIndicator
                      readOnly={readOnly}
                      index={index}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {!readOnly && (
                      <IconButton
                        aria-label={`${t("delete")} ${t("tesSalaryTablesColumns")}`}
                        onClick={() =>
                          setDraft((current) => {
                            const indexToRemove = current.columns.findIndex(
                              (item) => item.columnId === column.columnId,
                            );
                            if (indexToRemove < 0) return current;

                            setUndoDeleteState({
                              type: "column",
                              item: current.columns[indexToRemove],
                              index: indexToRemove,
                              cells: current.cells.filter(
                                (cell) => cell.columnId === column.columnId,
                              ),
                            });

                            return removeDraftColumn(current, column.columnId);
                          })
                        }
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      <Grid item xs={12}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
        >
          <Typography variant="h6">{t("tesSalaryTablesCells")}</Typography>
          {!readOnly && (
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setHourlyShortcutMode("amount")}
              >
                {t("tesSalaryTablesBulkChangeHourlyByAmount")}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setHourlyShortcutMode("percent")}
              >
                {t("tesSalaryTablesBulkChangeHourlyByPercent")}
              </Button>
            </Box>
          )}
        </Box>
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ marginTop: 1 }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("tesSalaryTablesRowColumn")}</TableCell>
                {draft.columns.map((column) => (
                  <TableCell key={column.columnId}>
                    {column.label || column.columnId}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {draft.rows.map((row) => (
                <TableRow key={row.rowId}>
                  <TableCell>{row.label || row.rowId}</TableCell>
                  {draft.columns.map((column) => {
                    const cell = getTesSalaryTableDraftCell(
                      draft.cells,
                      row.rowId,
                      column.columnId,
                    );
                    return (
                      <TableCell key={`${row.rowId}_${column.columnId}`}>
                        <Box display="flex" flexDirection="column" gap={1}>
                          {(salaryViewMode === "all" ||
                            salaryViewMode === "hourly") && (
                            <TesSalaryTableDecimalInput
                              label={t("talentProfile:hourlyWage")}
                              value={cell?.hourlySalary || ""}
                              disabled={readOnly}
                              onChange={(value) =>
                                setDraft((current) =>
                                  upsertTesSalaryTableDraftCell({
                                    draft: current,
                                    rowId: row.rowId,
                                    columnId: column.columnId,
                                    field: "hourlySalary",
                                    value,
                                    monthlyDivider: hasValidMonthlyDivider
                                      ? monthlyDividerNumber
                                      : null,
                                    monthlySalaryScale:
                                      roundCalculatedMonthlySalaryToFullEur
                                        ? 0
                                        : 2,
                                  }),
                                )
                              }
                            />
                          )}
                          {(salaryViewMode === "all" ||
                            salaryViewMode === "monthly") && (
                            <TesSalaryTableDecimalInput
                              label={t("talentProfile:monthlySalary")}
                              value={cell?.monthlySalary || ""}
                              disabled={readOnly}
                              onChange={(value) =>
                                setDraft((current) =>
                                  upsertTesSalaryTableDraftCell({
                                    draft: current,
                                    rowId: row.rowId,
                                    columnId: column.columnId,
                                    field: "monthlySalary",
                                    value,
                                    monthlyDivider: hasValidMonthlyDivider
                                      ? monthlyDividerNumber
                                      : null,
                                  }),
                                )
                              }
                            />
                          )}
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      <Dialog
        open={!!hourlyShortcutMode}
        onClose={() => {
          setHourlyShortcutMode(null);
          setHourlyShortcutValue("");
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {hourlyShortcutMode === "amount"
            ? t("tesSalaryTablesBulkChangeHourlyByAmount")
            : t("tesSalaryTablesBulkChangeHourlyByPercent")}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            margin="dense"
            value={hourlyShortcutValue}
            onChange={(event) => setHourlyShortcutValue(event.target.value)}
            label={
              hourlyShortcutMode === "amount"
                ? t("tesSalaryTablesBulkChangeAmountLabel")
                : t("tesSalaryTablesBulkChangePercentLabel")
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setHourlyShortcutMode(null);
              setHourlyShortcutValue("");
            }}
          >
            {t("common:cancel")}
          </Button>
          <Button variant="contained" onClick={applyHourlyShortcut}>
            {t("common:save")}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={!!undoDeleteState}
        autoHideDuration={6000}
        onClose={() => setUndoDeleteState(null)}
        message={t("common:deletedSuccessfully")}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              if (!undoDeleteState) return;

              setDraft((current) => {
                if (undoDeleteState.type === "row") {
                  if (
                    current.rows.some(
                      (row) => row.rowId === undoDeleteState.item.rowId,
                    )
                  ) {
                    return current;
                  }

                  const nextRows = [...current.rows];
                  nextRows.splice(
                    Math.min(undoDeleteState.index, nextRows.length),
                    0,
                    undoDeleteState.item,
                  );
                  return {
                    ...current,
                    rows: nextRows,
                    cells: [...current.cells, ...undoDeleteState.cells],
                  };
                }

                if (
                  current.columns.some(
                    (column) =>
                      column.columnId === undoDeleteState.item.columnId,
                  )
                ) {
                  return current;
                }

                const nextColumns = [...current.columns];
                nextColumns.splice(
                  Math.min(undoDeleteState.index, nextColumns.length),
                  0,
                  undoDeleteState.item,
                );
                return {
                  ...current,
                  columns: nextColumns,
                  cells: [...current.cells, ...undoDeleteState.cells],
                };
              });
              setUndoDeleteState(null);
            }}
          >
            {t("common:undo")}
          </Button>
        }
      />
    </DialogContentWrapper>
  );
};

export default TesSalaryTableEditorDialog;
